/**
 * Fresh filter controls for the site-dashboard alarm grid.
 *
 * A compact, always-visible filter row that replaces the old dropdown
 * FilterBar on the alarms card: a three-way segmented status control
 * (All / Active / Resolved). Free-text search stays owned by the grid
 * toolbar.
 *
 * Contains:
 * - STATUS_VALUES — the three status-segment values
 * - AlarmGridFilters — the segmented-status control (default export)
 */
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

export const STATUS_VALUES = Object.freeze({ ALL: 'all', ACTIVE: 'active', RESOLVED: 'resolved' });

const TOGGLE_BUTTON_SX = {
  px: 1.5,
  py: 0.25,
  fontSize: '0.75rem',
  lineHeight: 1.6,
  textTransform: 'none',
};

/**
 * @param {object}   props
 * @param {string}   props.statusFilter    - current status segment ('all' | 'active' | 'resolved')
 * @param {Function} props.onStatusChange  - fn(value) — invoked with the new status value
 * @param {Function} props.t - i18next translator
 */
function AlarmGridFilters({ statusFilter, onStatusChange, t }) {
  const handleStatusChange = (_event, value) => {
    if (value != null) onStatusChange(value);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={statusFilter}
        onChange={handleStatusChange}
        aria-label={t('general.status', 'Status')}
      >
        <ToggleButton value={STATUS_VALUES.ALL} sx={TOGGLE_BUTTON_SX}>
          {t('general.all', 'All')}
        </ToggleButton>
        <ToggleButton value={STATUS_VALUES.ACTIVE} sx={TOGGLE_BUTTON_SX}>
          {t('alarms.active', 'Active')}
        </ToggleButton>
        <ToggleButton value={STATUS_VALUES.RESOLVED} sx={TOGGLE_BUTTON_SX}>
          {t('alarms.resolved', 'Resolved')}
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}

AlarmGridFilters.propTypes = {
  statusFilter: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default AlarmGridFilters;
